import React from 'react';
import Section from '../layouts/Section';
import PageLayout from '../layouts/PageLayout';

const ReturnRefundPolicy = () => {
  return (
    <PageLayout title="Cell Station – Return & Refund Policy">
      <Section title="Return & Refund Policy">
        <div className="section">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="policy-content" style={styles.content}>
                  <p style={styles.lastUpdated}>Last Updated: January 2025</p>

                  <div style={styles.highlightBox}>
                    <h4 style={styles.highlightTitle}>
                      <i className="fas fa-info-circle" style={{ marginRight: '10px' }}></i>
                      Our Commitment
                    </h4>
                    <p style={styles.highlightText}>
                      At Cell Station, your satisfaction is our priority. We stand behind our repairs and
                      products. Please read our return and refund policies below to understand how we handle
                      different situations.
                    </p>
                  </div>

                  {/* Repair Services Section */}
                  <div style={styles.policySection}>
                    <div style={styles.sectionHeader}>
                      <span style={styles.sectionIcon}>
                        <i className="fas fa-tools"></i>
                      </span>
                      <h3 style={styles.sectionTitle}>Repair Services</h3>
                    </div>

                    <div style={styles.policyCard}>
                      <h4 style={styles.cardTitle}>Repair Satisfaction</h4>
                      <p style={styles.paragraph}>
                        If you are not satisfied with a repair, please contact us within <strong>7 days</strong> of
                        pickup. We will assess the issue and either re-repair the device at no additional cost
                        or provide a refund for the repair service.
                      </p>
                    </div>

                    <div style={styles.policyCard}>
                      <h4 style={styles.cardTitle}>Repair Refund Conditions</h4>
                      <ul style={styles.list}>
                        <li>Refunds are available if the repair did not resolve the original issue</li>
                        <li>New damage or issues unrelated to the original repair are not covered</li>
                        <li>Diagnostic fees are non-refundable if you decline the repair</li>
                        <li>Refunds are processed to the original payment method within 5-7 business days</li>
                      </ul>
                    </div>

                    <div style={styles.policyCard}>
                      <h4 style={styles.cardTitle}>Non-Refundable Repair Services</h4>
                      <ul style={styles.list}>
                        <li>Water damage repairs (due to unpredictable nature)</li>
                        <li>Repairs on devices with prior unauthorized modifications</li>
                        <li>Software services and data recovery</li>
                        <li>Devices with additional damage after leaving our store</li>
                      </ul>
                    </div>
                  </div>

                  {/* Accessories & Products Section */}
                  <div style={styles.policySection}>
                    <div style={styles.sectionHeader}>
                      <span style={styles.sectionIcon}>
                        <i className="fas fa-shopping-bag"></i>
                      </span>
                      <h3 style={styles.sectionTitle}>Accessories & Products</h3>
                    </div>

                    <div style={styles.policyCard}>
                      <h4 style={styles.cardTitle}>Return Window</h4>
                      <p style={styles.paragraph}>
                        Unused accessories and products may be returned within <strong>14 days</strong> of
                        purchase with original receipt and packaging.
                      </p>
                    </div>

                    <div style={styles.policyCard}>
                      <h4 style={styles.cardTitle}>Return Requirements</h4>
                      <ul style={styles.list}>
                        <li>Item must be in original, unopened packaging</li>
                        <li>Original receipt or proof of purchase required</li>
                        <li>Item must be in resalable condition</li>
                        <li>All original accessories and materials must be included</li>
                      </ul>
                    </div>

                    <div style={styles.policyCard}>
                      <h4 style={styles.cardTitle}>Non-Returnable Items</h4>
                      <ul style={styles.list}>
                        <li>Opened screen protectors and tempered glass</li>
                        <li>Earbuds and headphones (hygiene reasons)</li>
                        <li>Customized or special-order items</li>
                        <li>Clearance and final sale items</li>
                        <li>Gift cards</li>
                      </ul>
                    </div>
                  </div>

                  {/* Exchange Policy */}
                  <div style={styles.policySection}>
                    <div style={styles.sectionHeader}>
                      <span style={styles.sectionIcon}>
                        <i className="fas fa-exchange-alt"></i>
                      </span>
                      <h3 style={styles.sectionTitle}>Exchange Policy</h3>
                    </div>

                    <div style={styles.policyCard}>
                      <p style={styles.paragraph}>
                        We offer exchanges for defective products within <strong>30 days</strong> of purchase.
                        If the same item is not available, we will offer store credit or an alternative
                        product of equal value.
                      </p>
                      <ul style={styles.list}>
                        <li>Defective items will be exchanged for the same product when available</li>
                        <li>Proof of purchase required for all exchanges</li>
                        <li>Exchanges for different items may be subject to price adjustments</li>
                      </ul>
                    </div>
                  </div>

                  {/* Refund Process */}
                  <div style={styles.policySection}>
                    <div style={styles.sectionHeader}>
                      <span style={styles.sectionIcon}>
                        <i className="fas fa-credit-card"></i>
                      </span>
                      <h3 style={styles.sectionTitle}>Refund Process</h3>
                    </div>

                    <div style={styles.processSteps}>
                      <div style={styles.step}>
                        <span style={styles.stepNumber}>1</span>
                        <div>
                          <h5 style={styles.stepTitle}>Bring Item to Store</h5>
                          <p style={styles.stepText}>Visit our store with the item and original receipt</p>
                        </div>
                      </div>
                      <div style={styles.step}>
                        <span style={styles.stepNumber}>2</span>
                        <div>
                          <h5 style={styles.stepTitle}>Inspection</h5>
                          <p style={styles.stepText}>Our team will inspect the item and verify eligibility</p>
                        </div>
                      </div>
                      <div style={styles.step}>
                        <span style={styles.stepNumber}>3</span>
                        <div>
                          <h5 style={styles.stepTitle}>Processing</h5>
                          <p style={styles.stepText}>Approved refunds processed within 5-7 business days</p>
                        </div>
                      </div>
                    </div>

                    <div style={styles.refundNote}>
                      <p style={styles.noteText}>
                        <i className="fas fa-exclamation-triangle" style={{ color: '#f0ad4e', marginRight: '10px' }}></i>
                        <strong>Note:</strong> Cash purchases over $100 may be refunded by check.
                        Credit/debit card refunds will be returned to the original card.
                      </p>
                    </div>
                  </div>

                  <div style={styles.contactBox}>
                    <h4 style={styles.contactTitle}>Need to Return or Exchange?</h4>
                    <p style={styles.contactText}>
                      Visit us in store or contact us before your visit:
                    </p>
                    <p style={styles.contactText}>
                      <i className="fas fa-phone" style={styles.icon}></i>
                      <a href="tel:+12159393933" style={styles.link}>+1 (215) 939-3933</a>
                    </p>
                    <p style={styles.contactText}>
                      <i className="fas fa-envelope" style={styles.icon}></i>
                      <a href="mailto:icellstation1@gmail.com" style={styles.link}>icellstation1@gmail.com</a>
                    </p>
                    <p style={styles.contactText}>
                      <i className="fas fa-map-marker-alt" style={styles.icon}></i>
                      2500 W Moreland Rd, Space #1077, Willow Grove, PA 19090
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

const styles = {
  content: {
    backgroundColor: '#fff',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 5px 30px rgba(0,0,0,0.08)',
    marginBottom: '40px',
  },
  lastUpdated: {
    color: '#666',
    fontStyle: 'italic',
    marginBottom: '20px',
  },
  highlightBox: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    padding: '30px',
    borderRadius: '12px',
    marginBottom: '40px',
    color: '#fff',
  },
  highlightTitle: {
    color: '#fff',
    marginBottom: '15px',
    fontSize: '20px',
  },
  highlightText: {
    color: 'rgba(255,255,255,0.9)',
    lineHeight: '1.8',
    margin: 0,
  },
  policySection: {
    marginBottom: '40px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '2px solid #f0f0f0',
  },
  sectionIcon: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    backgroundColor: '#F50963',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    marginRight: '15px',
  },
  sectionTitle: {
    color: '#1a1a2e',
    fontSize: '24px',
    fontWeight: '600',
    margin: 0,
  },
  policyCard: {
    backgroundColor: '#f8f9fa',
    padding: '25px',
    borderRadius: '10px',
    marginBottom: '15px',
  },
  cardTitle: {
    color: '#1a1a2e',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '15px',
  },
  paragraph: {
    color: '#555',
    lineHeight: '1.8',
    marginBottom: '15px',
  },
  list: {
    color: '#555',
    lineHeight: '2',
    paddingLeft: '20px',
    margin: 0,
  },
  processSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '20px',
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '10px',
  },
  stepNumber: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#F50963',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '600',
    flexShrink: 0,
  },
  stepTitle: {
    color: '#1a1a2e',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '5px',
  },
  stepText: {
    color: '#666',
    margin: 0,
    fontSize: '14px',
  },
  refundNote: {
    backgroundColor: '#fff8e6',
    padding: '20px',
    borderRadius: '10px',
    border: '1px solid #f0e6cc',
  },
  noteText: {
    color: '#555',
    margin: 0,
    lineHeight: '1.6',
  },
  contactBox: {
    backgroundColor: '#f8f9fa',
    padding: '30px',
    borderRadius: '10px',
    borderLeft: '4px solid #F50963',
    marginTop: '40px',
  },
  contactTitle: {
    color: '#1a1a2e',
    marginBottom: '15px',
  },
  contactText: {
    color: '#555',
    marginBottom: '10px',
    lineHeight: '1.8',
  },
  link: {
    color: '#F50963',
    textDecoration: 'none',
  },
  icon: {
    color: '#F50963',
    marginRight: '10px',
    width: '20px',
  },
};

export default ReturnRefundPolicy;
